// ReplayButton's player (UI-C5, P-8, D-11, EM-23, T-304): replay the ORIGINAL recording of one phrase.
//  - fetch GET /api/audio/{segment_id} with `Authorization: Bearer <seat token>` - or, MAJLIS-67, the record's
//    credential (P-23) while a finished majlis's record is open (the token is never in a URL and
//    never reaches a log), to a blob, and play that blob's URL; the URL is revoked when playback ends, fails or stops;
//  - play-state per phrase: idle > loading > playing; EXACTLY ONE phrase plays at a time: starting one stops the other
//    (one element, one current phrase); tapping the phrase that is loading or playing stops it (aria-pressed), and a
//    tap after that fetches and plays it again from the start;
//  - every refusal is the same 404 {"code": "not_found"}: the phrase is marked `unavailable` for this session and never
//    fetched again (no retry, no loop). Any other failure (network, decode, a refused play()) shows `error` on that
//    phrase for REPLAY_ERROR_MS, then idle again, so another tap retries; nothing retries by itself (consultant
//    2026-09-20). `loading` HAS AN END: a play() that neither starts the sound nor refuses it is ended by the
//    fuse (PLAY_START_FUSE_MS, MAJLIS-12) as an error with its reason, so the button never waits for ever;
//  - the client is container-agnostic: it plays whatever Content-Type the server answers with (the blob keeps it). The
//    ONE place a container is named is the capability probe: phrase audio is WebM/Opus (consultant 2026-09-20; Replay
//    needs iOS/iPadOS/desktop Safari 17.5+, current Chrome, Firefox). The element is asked canPlayType ONCE; a browser
//    that answers "" gets a disabled Replay (`unsupported`) with its reason, never a button that does nothing;
//  - the sound goes through an <audio> element (routable later: M2 live-audio rooms may pick its output), never through
//    the microphone's AudioContext, so a replay cannot feed the mic path ELECTRICALLY. It plainly can ACOUSTICALLY:
//    on the owner's iPhone the speaker's output was heard by his own open microphone and came back as a new phrase.
//    So while a phrase plays, this device's microphone is gated (EM-25, media/micGate.ts) — the player holds the
//    gate from the moment play() is called until playback ends, is stopped, or fails.

import type { Clock, TimerId } from "../clock";
import { REPLAY_ERROR_MS } from "../constants";

/** UI-C5 states of one phrase in the player; `unavailable` and `unsupported` are read separately. */
export type ReplayState = "idle" | "loading" | "playing" | "error";

/** The subset of HTMLAudioElement the player uses (tests inject a fake). */
export interface AudioLike {
  src: string;
  canPlayType(type: string): string;
  onended: ((ev: Event) => void) | null;
  onerror: ((ev: Event | string) => void) | null;
  /** Something OTHER than us stopped the sound: an incoming call, Siri, headphones unplugged, a Bluetooth
   * drop, the lock screen's pause, another app taking the audio session. iOS does all of these routinely, and
   * none of them fires `ended` (EM-25: an unreleased gate would leave his microphone shut). */
  onpause: ((ev: Event) => void) | null;
  play(): Promise<void>;
  pause(): void;
  removeAttribute(name: string): void;
  load(): void;
}

/** URL.createObjectURL / URL.revokeObjectURL, injected. */
export interface BlobUrls {
  create(blob: Blob): string;
  revoke(url: string): void;
}

/** What the ReplayButton reads and calls. */
export interface ReplayControl {
  subscribe(fn: () => void): () => void;
  /** Changes whenever any phrase's state changes (useSyncExternalStore's snapshot). */
  version(): number;
  state(segmentId: string): ReplayState;
  unavailable(segmentId: string): boolean;
  /** Can this browser play phrase audio at all? Probed once. */
  supported(): boolean;
  /** Is this iOS/iPadOS (for the unsupported reason)? Asked once. */
  onIOS(): boolean;
  /** Is any phrase showing `error` now (the one polite announcement)? */
  failing(): boolean;
  toggle(segmentId: string): void;
}

export interface ReplayOptions {
  fetch: typeof fetch;
  /** The seat token (RoomClient.seatToken); null before the seat is joined. */
  seatToken: () => string | null;

  /** Creates the ONE element replay plays through (called once, lazily). */
  audio: () => AudioLike;
  urls: BlobUrls;
  /** Times the `error` look (Q-1: an injected clock). */
  clock: Clock;
  /** iOS/iPadOS detection (detectIOS in the app), injected. */
  isIOS: () => boolean;
  /** EM-25: shuts this device's microphone while a phrase is playing. Absent in tests that do not check it. */
  gate?: { acquire(): () => void };
  /** Diagnostics (Q-1: the failure with its body). Never receives the token or a blob URL. */
  log?: (message: string, body?: unknown) => void;
}

/** EM-25, ASSUMPTION: no legitimate playback outlasts this. A phrase is capped at 20 s (the VAD) plus its
 * padding, so 30 s is generous for the longest clip that can exist. After it the hold is force-released with a
 * warning: a gate that fails open is an echo, a gate that fails shut is a person talking to nobody. */
export const GATE_MAX_HOLD_MS = 30_000;

/** MAJLIS-12, UI-C5, EM-25, ASSUMPTION: play() has either started the sound or refused it within this.
 *
 * The fuse on the `loading` state. `play()` normally answers at once: the clip is already in memory (we fetched
 * it to a blob before calling), so starting it is a decode of the first frames, and a refusal — the autoplay
 * policy, an undecodable container — rejects immediately. But the promise can also do NEITHER: found in a
 * desktop Chrome session (the consultant's, 2026-09-20) where the tab's visibilityState was "hidden" and Chrome
 * deferred the media start, so `play()` never settled, the button sat on `loading` with no end, and the
 * microphone gate below was held until GATE_MAX_HOLD_MS. The cause does not matter; a `play()` that never
 * settles is the shape to survive.
 *
 * 5 s is not measured. It is two orders of magnitude above a local decode, so no real start is cut short, and
 * six times shorter than GATE_MAX_HOLD_MS, so a dead play() costs this seat five seconds of microphone rather
 * than thirty. A person who has tapped Replay and heard nothing has given up long before either. */
export const PLAY_START_FUSE_MS = 5_000;

/** The capability probe: the only place the container is named (a new container changes this line only). */
export const REPLAY_PROBE_TYPE = 'audio/webm; codecs="opus"';

/** iOS and iPadOS (which reports itself as a Mac with touch). */
export function detectIOS(nav: { userAgent: string; platform?: string; maxTouchPoints?: number }): boolean {
  return /\b(iPhone|iPad|iPod)\b/.test(nav.userAgent) || (nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1);
}

export const audioPath = (segmentId: string): string => `/api/audio/${encodeURIComponent(segmentId)}`;

interface Current {
  id: string;
  state: "loading" | "playing";
  gen: number;
  url: string | null;
  /** Releases the microphone gate this playback holds (EM-25); absent until play() is called. */
  ungate?: () => void;
  /** The watchdog that force-releases that hold if this playback never ends (EM-25). */
  watchdog?: TimerId;
  /** The fuse on `loading`: it ends this start if play() never says the sound began (MAJLIS-12). Cleared the
   * moment the state becomes `playing`, so a long phrase is the watchdog's business, not the fuse's. */
  fuse?: TimerId;
}

export class ReplayPlayer implements ReplayControl {
  private current: Current | null = null;
  private gone = new Set<string>();
  /** phrases showing `error`, each with the timer that returns it to idle */
  private errors = new Map<string, TimerId>();
  /** Bumped on every start and stop: a late answer for an older start is dropped, never played. */
  private gen = 0;
  private ver = 0;
  private el: AudioLike | null = null;
  private unlocked = false;
  private canPlay: boolean | null = null;
  private ios: boolean | null = null;
  private listeners = new Set<() => void>();
  /** MAJLIS-67 (P-23): see `setAuthorization`. */
  private authorization: string | null = null;

  constructor(private readonly opts: ReplayOptions) {}

  /** MAJLIS-67 (P-23): the WHOLE Authorization value to send instead of `Bearer <seat token>` - the record's credential
   * while a finished majlis's record is open; null (the default) goes back to the seat token, as live. Held in memory
   * only, never logged, never in a URL. */
  setAuthorization(value: string | null): void {
    this.authorization = value;
  }

  /** MAJLIS-ADMIN (P-37): where a clip is fetched from - the admin view's own path while it is open; null goes back to
   * GET /api/audio/{segment_id}. */
  private pathFor: ((segmentId: string) => string) | null = null;
  setAudioPath(fn: ((segmentId: string) => string) | null): void {
    this.pathFor = fn;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  version = (): number => this.ver;

  state(segmentId: string): ReplayState {
    if (this.current?.id === segmentId) return this.current.state;
    return this.errors.has(segmentId) ? "error" : "idle";
  }

  failing(): boolean {
    return this.errors.size > 0;
  }

  onIOS(): boolean {
    this.ios ??= this.opts.isIOS();
    return this.ios;
  }

  unavailable(segmentId: string): boolean {
    return this.gone.has(segmentId);
  }

  supported(): boolean {
    this.canPlay ??= this.element().canPlayType(REPLAY_PROBE_TYPE) !== "";
    return this.canPlay;
  }

  toggle(segmentId: string): void {
    if (this.current?.id === segmentId) return this.stop();
    if (this.gone.has(segmentId) || !this.supported()) return;
    this.start(segmentId);
  }

  /** Stop whatever plays (or loads) and release its blob URL. */
  stop(): void {
    const c = this.current;
    this.gen += 1;
    if (!c) return;
    this.current = null;
    this.release(c);
    this.emit();
  }

  /** A new room or the seat left: stop, and forget which phrases were unavailable or failing. */
  reset(): void {
    this.stop();
    if (this.gone.size || this.errors.size) {
      this.gone.clear();
      for (const id of this.errors.values()) this.opts.clock.clearTimeout(id);
      this.errors.clear();
      this.emit();
    }
  }

  // ------------------------------------------------------------------

  private start(id: string): void {
    this.stop(); // exactly one plays: starting one stops the other (T-304)
    this.clearError(id); // a tap on a failed phrase is the retry
    const seat = this.opts.seatToken();
    const token = this.authorization ?? (seat ? `Bearer ${seat}` : null);
    if (!token) {
      this.log("replay: no seat yet", { segment: id });
      this.flagError(id);
      this.emit();
      return;
    }
    const el = this.element();
    if (!this.unlocked) {
      // ASSUMPTION (iOS Safari), to be verified on the owner's iPhone: play() inside the tap unlocks this element for
      // the play() that follows the fetch, outside the gesture. With no source it rejects at once or when the real
      // source is set; either is expected. If the later play() is still refused on iOS, the fallback (NOT built;
      // consultant 2026-09-20) is to unlock once on the first user gesture in the room: resume a silent AudioContext
      // or play a tiny silent buffer.
      this.unlocked = true;
      el.play().catch(() => undefined);
    }
    const gen = ++this.gen;
    this.current = { id, state: "loading", gen, url: null };
    this.emit();
    void this.load(id, token, gen);
  }

  private async load(id: string, token: string, gen: number): Promise<void> {
    let res: Response;
    try {
      res = await this.opts.fetch((this.pathFor ?? audioPath)(id), {
        headers: { Authorization: token },
        cache: "no-store",
      });
    } catch (e) {
      return this.fail(gen, id, "replay: the recording could not be fetched", { error: errorName(e) });
    }
    if (gen !== this.gen) return; // stopped, or another phrase started, while this one was on its way
    if (res.status === 404) {
      // every refusal (EM-23): unavailable for this session, never fetched again
      return this.fail(gen, id, "replay: recording not available", { status: 404 }, "unavailable");
    }
    if (res.status !== 200) {
      return this.fail(gen, id, "replay: unexpected answer", { status: res.status });
    }
    let blob: Blob;
    try {
      blob = await res.blob();
    } catch (e) {
      return this.fail(gen, id, "replay: the recording could not be read", { error: errorName(e) });
    }
    if (gen !== this.gen) return;
    const url = this.opts.urls.create(blob);
    this.current = { id, state: "loading", gen, url };
    const el = this.element();
    el.onended = () => {
      if (gen === this.gen) this.stop();
    };
    el.onpause = () => {
      // A pause we did not ask for ends this playback: the sound has stopped, so the gate must open and the
      // button must return to idle. Our own stop() clears these handlers before it pauses, so this cannot
      // re-enter from there.
      if (gen === this.gen) {
        this.log("replay: playback paused from outside", { segment: id });
        this.stop();
      }
    };
    el.onerror = () => {
      this.fail(gen, id, "replay: the recording could not be played", {});
    };
    el.src = url;
    // EM-25: the gate goes on before the first sample and comes off in release(), whatever ends the playback.
    this.current = {
      id,
      state: "loading",
      gen,
      url,
      ungate: this.opts.gate?.acquire(),
      watchdog: this.opts.gate ? this.opts.clock.setTimeout(() => this.forceRelease(gen, id), GATE_MAX_HOLD_MS) : undefined,
      // MAJLIS-12: the fuse is armed with the gate, whether or not there is a gate to hold — a button stuck on
      // `loading` is wrong on its own, and a held microphone is what makes it urgent.
      fuse: this.opts.clock.setTimeout(() => this.fuseBlown(gen, id), PLAY_START_FUSE_MS),
    };
    try {
      await el.play();
    } catch (e) {
      return this.fail(gen, id, "replay: the recording could not be played", { error: errorName(e) });
    }
    // A late answer for a start that is over — stopped, replaced by another phrase, or given up on by the fuse —
    // is dropped here: `gen` has moved on, so nothing is resurrected and nothing is released twice (MAJLIS-12).
    if (gen !== this.gen) return;
    this.clearFuse();
    this.current = { ...this.current, id, state: "playing", gen, url, fuse: undefined };
    this.emit();
  }

  /** The start `gen` failed, if it is still the current one: log it (Q-1), stop, and mark the phrase `unavailable`
   * (a 404) or `error` (anything else, for REPLAY_ERROR_MS). */
  private fail(gen: number, id: string, message: string, body: Record<string, unknown>, as: "error" | "unavailable" = "error"): void {
    if (gen !== this.gen) return;
    this.log(message, { segment: id, ...body });
    this.stop();
    if (as === "unavailable") this.gone.add(id);
    else this.flagError(id);
    this.emit();
  }

  private flagError(id: string): void {
    this.clearError(id);
    const timer = this.opts.clock.setTimeout(() => {
      this.errors.delete(id);
      this.emit();
    }, REPLAY_ERROR_MS);
    this.errors.set(id, timer);
  }

  private clearError(id: string): void {
    const timer = this.errors.get(id);
    if (timer === undefined) return;
    this.opts.clock.clearTimeout(timer);
    this.errors.delete(id);
  }

  /** The watchdog: this playback outlasted any clip that can exist, so the hold is dropped whatever the
   * element thinks. Loud, because it means an `ended`, `pause` or `error` we expected never arrived. */
  /** MAJLIS-12: play() was called and never answered — no `playing`, no rejection, no element error. Without
   * this the button sits on `loading` for as long as the room lasts and the microphone stays gated until the
   * watchdog at GATE_MAX_HOLD_MS. End the start here with its reason; the hold comes off in release(), the one
   * path every other ending takes, so the gate's single writer (micGate.ts) is still the only one that writes.
   * The number printed is an ASSUMPTION, labelled here because it is printed (Q-1). */
  private fuseBlown(gen: number, id: string): void {
    this.fail(gen, id, "replay: the browser did not start playing the recording", {
      ms: PLAY_START_FUSE_MS,
      assumption: true,
    });
  }

  private clearFuse(): void {
    const c = this.current;
    if (!c || c.fuse === undefined) return;
    this.opts.clock.clearTimeout(c.fuse);
    c.fuse = undefined;
  }

  private forceRelease(gen: number, id: string): void {
    if (gen !== this.gen || !this.current) return;
    this.log("replay: gate held too long, releasing it", { segment: id, ms: GATE_MAX_HOLD_MS });
    this.stop();
  }

  private release(c: Current): void {
    if (c.watchdog !== undefined) this.opts.clock.clearTimeout(c.watchdog);
    if (c.fuse !== undefined) this.opts.clock.clearTimeout(c.fuse);
    c.ungate?.();
    const el = this.el;
    if (el) {
      el.onended = null;
      el.onerror = null;
      el.onpause = null;
      el.pause();
      if (c.url) {
        el.removeAttribute("src");
        el.load();
      }
    }
    if (c.url) this.opts.urls.revoke(c.url);
  }

  private element(): AudioLike {
    this.el ??= this.opts.audio();
    return this.el;
  }

  private emit(): void {
    this.ver += 1;
    for (const fn of this.listeners) fn();
  }

  private log(message: string, body?: unknown): void {
    this.opts.log?.(message, body);
  }
}

/** Only the error's name reaches a log: a message could carry anything. */
function errorName(e: unknown): string {
  return e instanceof Error ? e.name : typeof e;
}
